import { CurrencyEnum } from '@systemeio/types'
import { getCssRgbValue } from 'shared/utils/get-css-rgb-value'

/*
 * Every currency takes one color family: the 400 rung at rest and the 500 one under the cursor. The
 * currencies below keep a reserved family and anything else takes what is left over, so no two
 * series share a color. Tailwind emits a theme variable only when its full name shows up in a scanned file, so the
 * names stay spelled out: one assembled from a family and a shade reads as empty at runtime.
 */
const COLOR_FAMILIES = {
  blue: { fill: '--color-system-primary-400', hover: '--color-system-primary-500' },
  green: { fill: '--color-system-success-400', hover: '--color-system-success-500' },
  yellow: { fill: '--color-system-warning-400', hover: '--color-system-warning-500' },
  red: { fill: '--color-system-error-400', hover: '--color-system-error-500' },
} as const

type AffiliateChartColorFamily = keyof typeof COLOR_FAMILIES

const RESERVED_COLOR_FAMILIES: Partial<Record<CurrencyEnum, AffiliateChartColorFamily>> = {
  [CurrencyEnum.EURO]: 'blue',
  [CurrencyEnum.US_DOLLAR]: 'green',
  [CurrencyEnum.BRAZILIAN_REAL]: 'red',
}

const getColorFamily = (
  currency: CurrencyEnum,
  allCurrencies: CurrencyEnum[],
): AffiliateChartColorFamily => {
  const reserved = RESERVED_COLOR_FAMILIES[currency]

  if (reserved) return reserved

  const available = (Object.keys(COLOR_FAMILIES) as AffiliateChartColorFamily[]).filter(
    family => !allCurrencies.some(cur => RESERVED_COLOR_FAMILIES[cur] === family),
  )
  const currencyIndex = allCurrencies.indexOf(currency)

  return available[Math.floor(Number(`0.${currencyIndex}`) * available.length)] ?? 'yellow'
}

export const getAffiliateChartColors = (currency: CurrencyEnum, allCurrencies: CurrencyEnum[]) => {
  const { fill, hover } = COLOR_FAMILIES[getColorFamily(currency, allCurrencies)]

  return { fill: getCssRgbValue(fill), hover: getCssRgbValue(hover) }
}

export const getAffiliateChartTextColor = (
  currency: CurrencyEnum,
  allCurrencies: CurrencyEnum[],
) => ({ color: getAffiliateChartColors(currency, allCurrencies).hover })
